<template>
  <popup-modal ref="popup">
    <div class="fb">
      <h5 class="fb__title">Gi tilbakemelding</h5>
      <p class="fb__lead">
        Vi leser alt. Vi rekker ikke alltid å svare, men det du skriver her er det vi jobber etter.
      </p>

      <div class="fb__kinds">
        <label class="fb__kind" :class="{ 'fb__kind--on': kind === 'bug' }">
          <input type="radio" value="bug" v-model="kind"> Noe er feil
        </label>
        <label class="fb__kind" :class="{ 'fb__kind--on': kind === 'idea' }">
          <input type="radio" value="idea" v-model="kind"> Forslag
        </label>
      </div>

      <textarea
          ref="message"
          class="form-control fb__text"
          rows="6"
          maxlength="4000"
          :placeholder="placeholder"
          v-model="message"></textarea>

      <!--
        Shown, not hidden. What is attached is the half that makes a report usable, and a merchant who
        cannot see what they are sending is right to be uneasy about sending it.
      -->
      <p class="fb__context">
        Sendes med: nettsiden du står på ({{ context.route || '—' }}), versjon
        {{ context.appVersion || '—' }}, nettleser og brukernavn.
        <span v-if="context.lastError"> Vi tar også med den siste tekniske feilmeldingen.</span>
      </p>

      <p v-if="error" class="fb__error">{{ error }}</p>

      <div class="fb__actions">
        <button type="button" class="bo_btn_secondary" :disabled="sending" @click="close">Avbryt</button>
        <button type="button" class="bo_btn" :disabled="sending || !message.trim()" @click="send">
          {{ sending ? 'Sender…' : 'Send' }}
        </button>
      </div>
    </div>
  </popup-modal>
</template>

<script>
import PopupModal from "@/components/other/PopupModal.vue";
import FeedbackService from "@/services/feedback/FeedbackService";
import { lastClientError } from "@/util/LastError";

/**
 * A merchant telling us something, from wherever they are standing.
 *
 * <b>The form is the cheap half.</b> «Knappen virker ikke» is worthless on its own; the same sentence
 * with the route, the version, the browser and the last JavaScript error is a bug report. So the
 * context is collected here rather than asked for — and shown, because a merchant who cannot see what
 * they are sending is right to hesitate.
 *
 * <b>What it promises.</b> «Vi leser alt. Vi rekker ikke alltid å svare.» A feedback button implies
 * somebody reads and replies, and feedback that vanishes teaches people that speaking up is pointless
 * — which costs the channel and the goodwill together. So the text says what actually happens.
 */
export default {
  name: 'FeedbackModal',
  components: { PopupModal },
  data() {
    return {
      kind: 'bug',
      message: '',
      sending: false,
      error: '',
      context: { route: '', appVersion: '', userAgent: '', lastError: '', locationId: '' }
    };
  },
  computed: {
    placeholder() {
      return this.kind === 'bug'
          ? 'Hva prøvde du å gjøre, og hva skjedde i stedet?'
          : 'Hva ville gjort hverdagen din enklere?';
    }
  },
  methods: {
    async open() {
      this.kind = 'bug';
      this.message = '';
      this.error = '';
      this.sending = false;
      this.context = {
        route: window.location.pathname + window.location.search,
        appVersion: await this.readVersion(),
        userAgent: navigator.userAgent,
        lastError: lastClientError(),
        locationId: localStorage.getItem('orders_selected_location') || ''
      };
      this.$refs.popup.open();
      this.$nextTick(() => this.$refs.message && this.$refs.message.focus());
    },

    /** The deployed build, from the same file the auto-reload check already polls. */
    async readVersion() {
      try {
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        return res.ok ? String((await res.json()).version || '') : '';
      } catch (e) {
        return '';
      }
    },

    close() {
      this.$refs.popup.close();
    },

    send() {
      if (!this.message.trim() || this.sending) {
        return;
      }
      this.sending = true;
      this.error = '';
      FeedbackService.send({ kind: this.kind, message: this.message, ...this.context }).then(
          (res) => {
            this.sending = false;
            if (res.data && res.data.result === 'SUCCEEDED') {
              this.close();
              this.$emit('sent');
            } else {
              this.error = 'Kunne ikke sende. Prøv igjen, eller send oss en e-post på support@cagista.com.';
            }
          },
          (err) => {
            console.log(err);
            this.sending = false;
            this.error = 'Kunne ikke sende. Prøv igjen, eller send oss en e-post på support@cagista.com.';
          }
      );
    }
  }
};
</script>

<style scoped>
/* Fills the window rather than sizing itself. PopupModal is `max-width: 480px` and shared with every
   other modal in the app, so a wider form here did not widen the box — it overflowed it, and the
   second choice and the textarea hung outside the white panel. */
.fb { width: 100%; padding: 4px 2px; }
.fb__title { margin: 0 0 4px; font-weight: 700; }
.fb__lead { margin: 0 0 14px; color: var(--bo-muted); font-size: 0.9rem; }

.fb__kinds { display: flex; gap: 0.5rem; margin-bottom: 10px; }

/* min-width:0 lets a flex item shrink below its content; without it two equal buttons refuse to fit. */
.fb__kind { min-width: 0; }

.fb__kind {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 8px 12px;
  border: 1px solid var(--bo-line);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.92rem;
}

.fb__kind--on { border-color: #ECB109; background: var(--bo-brand-tint); font-weight: 600; }

/* Vertical only, and never wider than the window whatever the browser decides to allow. */
.fb__text { resize: vertical; max-width: 100%; }

.fb__context { margin: 10px 0 0; font-size: 0.78rem; color: var(--bo-muted); }
.fb__error { margin: 8px 0 0; font-size: 0.85rem; color: var(--bo-bad-fg); }

.fb__actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 14px; }
</style>
